import { match } from "ts-pattern";
import { ValueOf } from "@/lib/utils";

export const MessageRole = {
  USER: "user",
  ASSISTANT: "assistant",
  AGENT_RUN_STATUS: "agent_run_status",
  TOOL_CALL: "tool_call",
  TOOL_RESPONSE: "tool_response",
  ASSISTANT_RESPONSE_END: "assistant_response_end",
  COMPACTION: "compaction",
};

export type MessageRole = ValueOf<typeof MessageRole>;

export interface ToolCall {
  id: string;
  toolName: string;
  parameters: Record<string, unknown>;
}

export interface ToolResult {
  id: string;
  toolName: string;
  result: Record<string, unknown>;
  status: "completed" | "error";
}

export class AgentMessage {
  constructor(
    public id: string,
    public threadId: string,
    public role: MessageRole,
    public content: Record<string, unknown>,
    public createdAt: Date,
    public metadata: Record<string, unknown> | null = null,
  ) {}

  static createOptimisticMessage(threadId: string, content: string): AgentMessage {
    return new AgentMessage(
      `temp-${Date.now()}`,
      threadId,
      "user",
      { "message": content },
      new Date(),
    );
  }

  // メッセージのテキスト内容を取得
  get textContent(): string {
    return match(this.role)
      .with("user", "assistant", "assistant_response_end", () => this.content["message"] as string)
      .otherwise(() => JSON.stringify(this.content));
  }

  //  表示用の短縮コンテンツを取得
  get preview(): string {
    const maxLength = 50;
    const text = this.textContent;
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  }

  // ツール呼び出しがあるかどうか
  get hasToolCall(): boolean {
    return this.role === "tool_call";
  }

  // ユーザーメッセージかどうか
  get isUser(): boolean {
    return this.role === "user";
  }

  // アシスタントメッセージかどうか
  get isAssistant(): boolean {
    return this.role === "assistant";
  }

  // コンパクションメッセージかどうか
  get isCompaction(): boolean {
    return this.metadata?.type === "compaction";
  }

  // サブエージェントからの呼び出しかどうか
  get isSubAgentCall(): boolean {
    return this.metadata?.author !== "VoiceekAgent";
  }

  // サブエージェントの最終レスポンスかどうか
  get isSubAgentFinalResponse(): boolean {
    return this.role === "assistant_response_end" && this.metadata?.author !== "VoiceekAgent";
  }

  // メインエージェントの最終レスポンスかどうか
  get isMainAgentFinalResponse(): boolean {
    return this.role === "assistant_response_end" && this.metadata?.author === "VoiceekAgent";
  }

  get toolCall(): ToolCall | null {
    if (this.role !== "tool_call") return null;

    return {
      id: this.content.id as string,
      toolName: this.content.name as string,
      parameters: this.content.args as Record<string, unknown>,
    };
  }

  get toolResult(): ToolResult | null {
    if (this.role !== "tool_response") return null;

    if (!this.metadata) {
      console.error("Tool response message missing metadata:", this);
      return null;
    }

    return {
      id: this.metadata.function_call_id as string,
      toolName: this.metadata.function_name as string,
      result: this.content,
      status: "completed",
    };
  }

  // write_tasksツールのタスクリストを取得
  get writeTasksList(): Array<{ content: string; status: string; }> | null {
    if (this.role !== "tool_call") return null;
    if (this.toolCall?.toolName !== "write_tasks") return null;

    const tasks = this.toolCall.parameters.tasks;
    if (!Array.isArray(tasks)) return null;

    return tasks as Array<{ content: string; status: string; }>;
  }

  /**
   * 相対的な作成時間を取得
   */
  get relativeCreatedAt(): string {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - this.createdAt.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return this.createdAt.toLocaleDateString();
  }
}
